<template>
  <div
    v-if="showSection"
    class="dn-section"
  >
    <dn-edition-collection
      :id="section.id"
      :options="{ publication,...sectionOptions }"
      class="dn-section-collection"
    >
      <template #edition-headline>
        <dn-headline
          class="header"
          :text="headline"
          tag="h1"
        />
      </template>
    </dn-edition-collection>
  </div>
  <div
    v-else
    class="dn-category"
  >
    <dn-collection
      :cid="sectionName"
      :provider="provider"
      :options="{ ...categoryOptions, 'categories': sectionName }"
      class="dn-category-collection"
      mood="append"
    >
      <template #default="{collection}">
        <div
          v-if="shouldShowAd(topBanner) && !topBanner.aboveHeader"
          class="top-ad-placeholder"
        >
          <dn-ad
            :ad="topBanner"
            class="full top-ad-below-header"
            data-zone="top-ad"
          />
        </div>
        <section
          v-if="collection && collection.length > 0"
          class="dn-collection-top"
        >
          <dn-headline
            class="header"
            :text="headline"
            tag="h1"
          />
          <template
            v-for="(group, index) in groupedCollectionByLayout(collection, 'abb,bbb,bb,bbb,b,bbb', 'bbb,bb')"
            :key="index"
          >
            <template v-if="(ad = getAdForIndex(sectionAdConfig?.inline || [], index)) && shouldShowAd(ad)">
              <dn-ad :ad="ad" />
            </template>
            <dn-set
              :layout="group.layout"
              :items="group.items.map((item) => ({id: item.id, type: item.type, hide_lead: true}))"
            />
          </template>
          <dn-collection-load-more
            :cid="sectionName"
            :size="categoryOptions.hits"
            :total="total"
          />
        </section>
        <template
          v-else-if="!loading"
        >
          {{ emptyCollection() }}
        </template>
      </template>
    </dn-collection>
  </div>
</template>
<script lang="ts" setup>
const nuxtApp = useNuxtApp();

definePageMeta({
  path: '/:section([A-Za-zæøåÆØÅ&_%C3%A6%C3%B8%C3%A5%C3%86%C3%98%C3%85]+$)',
  layout: 'default'
});
const loading = ref(false);
provide('loading', loading);
const sectionName = typeof useRoute().params.section === 'string'
  ? decodeURIComponent(useRoute().params.section as string)
  : useRoute().params.section && useRoute().params.section.length > 0
    ? decodeURIComponent(useRoute().params.section[0] as string)
    : '';
const showSection = ref(false);

const headline = computed(() => {
  return sectionName.replace(/_/g, ' ')
    .toUpperCase();
});

const { name: publication, section: { options: sectionOptions }, category: { options: categoryOptions, provider }, showEditionContent } = useRuntimeConfig().public;
const section = useRuntimeConfig().public.config?.sections?.find(section => section.name === sectionName);

const { section: sectionAdConfig } = getAdConfig();
const topBanner = sectionAdConfig?.static?.topBanner;

const total = ref(0);
if (section) {
  showSection.value = true;
}

provide('teaser_provider', 'articleTeaser');
provide('zone_type', 'section-list');
//  todo: read from config
provide('teaser_asset_default_ratio', '16x9');
provide('embed_edition_content', showEditionContent !== false);
useHead({
  title: headline
});

const emptyCollection = () => {
  throw createError({
    statusCode: 404,
    message: 'No articles found for this category'
  });
};
const listenerCleanup = useBusListener({
  [`DnCollection__collection__${sectionName}`]: (data) => {
    total.value = data.total;
  }
});

onUnmounted(() => {
  if (listenerCleanup) {
    listenerCleanup();
  }
});
const pageType = computed(() => {
  return showSection.value ? 'sectionpage' : 'categorypage';
});

nuxtApp.callHook('cpp:data:loaded', { type: pageType.value, name: sectionName, title: headline.value });
</script>

<style lang="scss">
.dn-section,
.dn-category {
  display: grid;
  grid-template: auto auto / repeat(1, 1fr);
  gap: var(--row-gap);
  inline-size: min(var(--grid-width), 100vw - var(--site-padding) * 2);
  padding-bottom: calc(var(--row-gap) * 2);
  padding-top: var(--row-gap);
  margin: 0 auto;

  .dn-collection {
    --dn-grid_column-gap: var(--row-gap);
    --dn-grid_row-gap: var(--row-gap);
    --dn-edition-collection_gap: var(--row-gap);
    --dn-collection_gap: var(--row-gap);
    --dn-collection--header_font-family: var(--typography-editorial-subhead-min-font-family);
    --dn-collection--header_font-weight: var(--typography-editorial-subhead-min-font-weight);
    --dn-collection--header_font-size: var(--typography-editorial-subhead-min-font-size);
    --dn-collection--header_line-height: var(--typography-editorial-subhead-min-line-height);
    --dn-collection--header_border-bottom-width: 1px;
    --dn-collection--header_border-bottom-color: var(--color-page-divider);
    --dn-collection--header_spacing: 0 0 var(--ui-gap);
  }
}

.dn-category {
  .dn-collection {
    --dn-collection-content-width: var(--tag-topic-category-top-content-width);
    --dn-collection-top_content-width: var(--tag-topic-category-top-content-width);

    @media only screen and (width >= 1300px) {
      --dn-collection_grid-template: var(--grid-template-common);
      --dn-collection-top_grid-area: var(--center_grid-area);
    }
  }
}
</style>
